import React, { useMemo } from 'react'
import * as yup from 'yup'

import { FormattedMessage, useIntl } from '@divvy-web/i18n'

import { useAuth } from '@sputnik/auth'
import { type NewCollaborator } from '@sputnik/hooks/useApplicationCollaborators/types'
import { matchEmail } from '@sputnik/utils/dataUtils'

interface UseInviteCollaboratorSchemaParams {
  collaborators: NewCollaborator[]
}

export const useInviteCollaboratorSchema = ({ collaborators }: UseInviteCollaboratorSchemaParams) => {
  const { email: loggedInEmail } = useAuth()
  const { formatMessage } = useIntl()

  const collaboratorSchema = useMemo(
    () =>
      yup.object().shape({
        id: yup.string().nullable(),
        firstName: yup.string().required(
          formatMessage({
            id: 'sputnik.InviteCollaboratorsDrawer__FZI5xl',
            defaultMessage: 'First name is required',
          }),
        ),
        lastName: yup.string().required(
          formatMessage({
            id: 'sputnik.InviteCollaboratorsDrawer__yos4hi',
            defaultMessage: 'Last name is required',
          }),
        ),
        collabRole: yup
          .string()
          .oneOf(['admin', 'collaborator'])
          .test(
            'maxAdmins',
            () => (
              <FormattedMessage
                defaultMessage='Only 3 admins are allowed'
                id='sputnik.InviteCollaboratorsDrawer__4Bglol'
              />
            ),
            (value, ctx) => {
              if (value !== 'admin') return true
              const editingId = ctx?.parent?.id
              const adminCount = collaborators.filter(({ createAsAdmin, id }) => {
                if (!createAsAdmin) return false
                if (editingId != null && id === editingId) return false
                return true
              }).length
              return adminCount < 3
            },
          )
          .required(),
        email: yup
          .string()
          .email(
            formatMessage({
              defaultMessage: 'Enter a valid email address',
              id: 'sputnik.useNewInviteCollaboratorsSchema__YRe93Y',
            }),
          )
          .test(
            'isInvitingSelf',
            () => (
              <FormattedMessage
                defaultMessage='You cannot invite yourself as a collaborator'
                id='sputnik.InviteCollaboratorsDrawer__wHFPaE'
              />
            ),
            (value) => {
              if (!value) return true
              if (value.toLowerCase() === loggedInEmail?.toLowerCase()) return false
              return true
            },
          )
          .test(
            'isNewEmail',
            () => (
              <FormattedMessage
                defaultMessage='Entered email is already a collaborator'
                id='sputnik.useNewInviteCollaboratorsSchema__kcLl0y'
              />
            ),
            (value, ctx) => {
              if (!value) return true
              const editingCollaboratorId = ctx?.parent?.id
              const editingCollaboratorEmail = editingCollaboratorId
                ? collaborators.find(({ id }) => id === editingCollaboratorId)?.email
                : undefined
              if (editingCollaboratorEmail && matchEmail(value, editingCollaboratorEmail)) return true
              return !collaborators
                .map(({ email }) => email.toLowerCase())
                .some((email) => email === value?.toLowerCase())
            },
          )
          .required(),
      }),
    [collaborators, loggedInEmail, formatMessage],
  )

  return collaboratorSchema
}

export type CollaboratorSchemaType = yup.InferType<ReturnType<typeof useInviteCollaboratorSchema>>
